import { type ColumnDef } from "@tanstack/react-table";
import { type TFunction } from "i18next";

import { RelativeDateCell } from "@/components/RelativeDateCell";
import { formatDateTimeCompact, formatRelativeDateTime } from "@/utils/relative-time";

import type { EntityId, InfoHubSubjectRow, SubjectActions, SubjectUser } from "../../types";
import { SortableColumnHeader } from "./SortableColumnHeader";
import { SubjectActionsCell } from "./SubjectActionsCell";

export interface CountryOption {
  id: EntityId;
  name: string;
}

export interface SubjectColumnMeta {
  cellClassName?: string;
  headerClassName?: string;
}

export type SubjectColumnDef = ColumnDef<InfoHubSubjectRow, unknown> & {
  meta?: SubjectColumnMeta;
};

const getCreationUserName = (user?: SubjectUser | null) =>
  [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "System";

const getCreatedTimestamp = (row: InfoHubSubjectRow) => {
  if (!row.creation_datetime) {
    return undefined;
  }
  const timestamp = new Date(row.creation_datetime).getTime();
  return Number.isNaN(timestamp) ? undefined : timestamp;
};

export const createColumns = (
  t: TFunction,
  actions: SubjectActions,
  isAllCountries: boolean,
  countries: CountryOption[] = []
): SubjectColumnDef[] => {
  const actionsColumn: SubjectColumnDef = {
    id: "actions",
    header: () => null,
    cell: ({ row }) => (
      <SubjectActionsCell
        subject={row.original}
        actions={actions}
        isAllCountries={isAllCountries}
        t={t}
      />
    ),
    enableSorting: false,
    meta: {
      cellClassName: "w-px whitespace-nowrap p-3",
      headerClassName: "w-px",
    },
  };

  const countryColumn: SubjectColumnDef = {
    id: "country",
    accessorKey: "country",
    header: () => (
      <span className="font-medium text-foreground">{t("infoHub.table.column.country")}</span>
    ),
    cell: ({ row }) => {
      const match = countries.find(country => country.id === row.original.country);
      return <span className="text-muted-foreground">{match ? match.name : "---"}</span>;
    },
    enableSorting: false,
    meta: { cellClassName: "w-40 p-3", headerClassName: "w-40" },
  };

  return [
    actionsColumn,
    {
      id: "created",
      accessorFn: getCreatedTimestamp,
      header: ({ column }) => (
        <SortableColumnHeader column={column} label={t("infoHub.table.column.created")} />
      ),
      cell: ({ row }) => (
        <div className="flex flex-col">
          <RelativeDateCell
            relative={formatRelativeDateTime(row.original.creation_datetime, t)}
            absolute={formatDateTimeCompact(row.original.creation_datetime)}
          />
          <span className="text-xs text-muted-foreground">
            {t("configuration.byFullName", {
              fullName: getCreationUserName(row.original.creation_user),
            })}
          </span>
        </div>
      ),
      sortDescFirst: true,
      sortingFn: "basic",
      sortUndefined: "last",
      meta: { cellClassName: "w-52 p-3", headerClassName: "w-52" },
    },
    ...(isAllCountries ? [countryColumn] : []),
    {
      id: "name",
      accessorKey: "name",
      header: () => (
        <span className="font-medium text-foreground">{t("infoHub.table.column.subject")}</span>
      ),
      cell: ({ row }) => <span className="font-medium text-foreground">{row.original.name}</span>,
      enableSorting: false,
      meta: { cellClassName: "min-w-[16rem] p-3" },
    },
    {
      id: "content_type",
      accessorKey: "content_type",
      header: () => (
        <span className="font-medium text-foreground">{t("infoHub.table.column.type")}</span>
      ),
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.content_type}</span>,
      enableSorting: false,
      meta: { cellClassName: "w-48 p-3" },
    },
    {
      id: "topics",
      accessorKey: "topics",
      header: () => (
        <span className="font-medium text-foreground">{t("infoHub.table.column.topics")}</span>
      ),
      cell: ({ row }) => {
        const topics = row.original.topics ?? [];
        if (topics.length === 0) {
          return <span className="text-muted-foreground">-</span>;
        }
        return (
          <ul className="list-disc space-y-0.5 pl-4 text-muted-foreground">
            {topics.map(topic => (
              <li key={topic.id}>{topic.name}</li>
            ))}
          </ul>
        );
      },
      enableSorting: false,
      meta: { cellClassName: "w-56 p-3 align-top" },
    },
    {
      id: "sent_to_count",
      accessorKey: "sent_to_count",
      header: () => (
        <span className="block text-right font-medium text-foreground">
          {t("infoHub.table.column.sentTo")}
        </span>
      ),
      cell: ({ row }) => (
        <span className="block text-right tabular-nums">{row.original.sent_to_count ?? 0}</span>
      ),
      enableSorting: false,
      meta: { cellClassName: "w-28 p-3", headerClassName: "w-28" },
    },
    {
      id: "read_count",
      accessorKey: "read_count",
      header: () => (
        <span className="block text-right font-medium text-foreground">
          {t("infoHub.table.column.read")}
        </span>
      ),
      cell: ({ row }) => (
        <span className="block text-right tabular-nums">{row.original.read_count ?? 0}</span>
      ),
      enableSorting: false,
      meta: { cellClassName: "w-24 p-3", headerClassName: "w-24" },
    },
  ];
};

export default createColumns;
